/**
 * How a deal-screening value, source or timestamp is written on screen.
 *
 * The memo, the gaps table and the workflow page all render the same facts, so
 * the wording lives here once: a figure written "$13.2M" in the header block
 * and "13.20" in the gaps table would read as two different numbers.
 *
 * Units come from the seam (`contracts.UNITS`): `usd_m` is USD millions,
 * `pct` is a FRACTION on the wire (0.362 → "36.2%"), `x` a multiple, `count` a
 * whole number, `text` a string value.
 */

import { stripMarks } from "./inlineMarks";
import { formatEpochMs } from "../Monitoring/monitoringFormat";
import type {
	DealScreeningFact,
	DealScreeningFactValue,
	DealScreeningSource,
} from "../../types/dealScreening";

/** The seam's value for a field no file answered. It IS the value, not a blank. */
export const NOT_FOUND = "Not found in provided materials";

/** What an absent table cell renders as. Blank would read as zero. */
export const EMPTY_VALUE = "—";

const isNumber = (value: DealScreeningFactValue): value is number =>
	typeof value === "number" && Number.isFinite(value);

const withGrouping = (value: number, decimals: number): string =>
	value.toLocaleString("en-US", {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});

/**
 * Money in millions to exactly one decimal (DOP-636): 18.43535 prints
 * "18.4", 6.79 prints "6.8", 4 prints "4.0". Half rounds up on the number
 * as written — 1.65 is "1.7", though its float is 1.6499… and
 * `toLocaleString` alone would print "1.6". Mirrors the backend's
 * `ledger.millions_text`; the citation carries the source's own digits.
 */
const money = (value: number): string =>
	withGrouping(Math.sign(value) * Math.round(Math.abs(value) * 10) / 10, 1);

/**
 * A magnitude in millions as its printed amount and scale: 4.12 → "4.1M",
 * 1,234.5 → "1.2B", 0.025 → "25.0K" — $M, with $B from a thousand million and
 * $K under a tenth of a million, always one decimal. The scale is chosen on
 * the rounded amount, so 999.96 is "1.0B". Mirrors the backend's
 * `money_units.scaled_millions`.
 */
export function scaledMillions(value: number): string {
	const size = Math.abs(value);
	if (Math.round(size * 10) / 10 >= 1000) return `${money(size / 1000)}B`;
	if (size > 0 && size < 0.1 && money(size * 1000) !== "100.0") return `${money(size * 1000)}K`;
	return `${money(size)}M`;
}

/**
 * The same figure as memos written before the $M rule print it: "$13.2mm",
 * and before DOP-636 two decimals with a trailing zero trimmed. Only for
 * finding it in their text.
 */
const legacyMoney = (value: number): string =>
	value.toLocaleString("en-US", {
		minimumFractionDigits: 1,
		maximumFractionDigits: 2,
	});

/**
 * Every way a memo may have written a money figure in running text: the
 * current rule ("$13.2M"), and the "mm" spellings older memos were written in.
 */
export function moneyTextVariants(value: number, unit: string): string[] {
	if (unit !== "usd_m") return [];
	const current = scaledMillions(value);
	const one = money(Math.abs(value));
	const body = legacyMoney(Math.abs(value));
	return value < 0
		? [`($${current})`, `-$${current}`, `($${one}mm)`, `($${body}mm)`, `(${body})`]
		: [`$${current}`, `$${one}mm`, `$${body}mm`, body];
}

/**
 * How a currency is written before a figure (DOP-658): the symbol where one
 * is standard and unambiguous, otherwise the ISO code and a space. One
 * spelling everywhere; mirrors the backend's `money_units.CURRENCY_PREFIXES`.
 */
const CURRENCY_PREFIXES: Record<string, string> = {
	USD: "$",
	CAD: "C$",
	AUD: "A$",
	NZD: "NZ$",
	HKD: "HK$",
	SGD: "S$",
	GBP: "£",
	EUR: "€",
};

/** "$" for USD or no currency, "C$" for CAD, "SEK " for one with no symbol. */
export function currencyPrefix(currency?: string | null): string {
	if (!currency) return "$";
	const code = currency.toUpperCase();
	return CURRENCY_PREFIXES[code] ?? `${code} `;
}

/** The prefix as a word in a note or a table corner: "C$", "SEK". */
export const currencyMark = (currency?: string | null): string =>
	currencyPrefix(currency).trim();

/** "C$ (CAD)" for a currency written with a symbol, "SEK" for one written as its code. */
export function currencyMarkAndCode(currency: string): string {
	const mark = currencyMark(currency);
	return mark === currency ? mark : `${mark} (${currency})`;
}

/**
 * A fact's value in running text: "$13.2M", "$1.2B", "36.2%", "4.2x", "212".
 * Null is the em dash; the caller decides whether that means NOT_FOUND.
 */
export function formatFactValue(
	value: DealScreeningFactValue,
	unit: string,
	/** The currency a money figure is STILL in when no rate converted it: "C$6.8M". */
	currency?: string | null,
): string {
	if (value === null || value === undefined) return EMPTY_VALUE;
	// Words, without the formatting written into them (DOP-660): this is
	// the plain reading; the memo draws the marks (`renderInline`).
	if (!isNumber(value)) return stripMarks(String(value));
	switch (unit) {
		case "usd_m": {
			const prefix = currencyPrefix(currency);
			return value < 0
				? `(${prefix}${scaledMillions(value)})`
				: `${prefix}${scaledMillions(value)}`;
		}
		case "pct":
			return `${withGrouping(value * 100, 1)}%`;
		case "x":
			return `${withGrouping(value, 1)}x`;
		case "count":
			return withGrouping(value, 0);
		case "per_share": {
			// EPS, NAV per share, a share price (DOP-531): whole currency units.
			const prefix = currencyPrefix(currency);
			return `${value < 0 ? "-" : ""}${prefix}${withGrouping(Math.abs(value), 2)} per share`;
		}
		default:
			return withGrouping(value, 1);
	}
}

/**
 * A value in a financial-summary cell, where the row header already says the
 * unit: "13.2", "(1.2)", "36.2%", "(29.0%)". Negatives in parentheses, the way the
 * firm's own memos write them.
 */
export function formatTableValue(
	value: DealScreeningFactValue,
	unit: string,
	/** The currency a per-share figure is still in when no rate converted it. */
	currency?: string | null,
): string {
	if (value === null || value === undefined) return EMPTY_VALUE;
	if (!isNumber(value)) return stripMarks(String(value));
	// A per-share figure keeps its currency and its "/sh" even in a cell,
	// where the table's "$M" corner would otherwise claim the wrong scale.
	const perShare = `${currencyPrefix(currency)}${withGrouping(Math.abs(value), 2)}/sh`;
	const body =
		unit === "pct"
			? `${withGrouping(Math.abs(value) * 100, 1)}%`
			: unit === "x"
				? `${withGrouping(Math.abs(value), 1)}x`
				: unit === "count"
					? withGrouping(Math.abs(value), 0)
					: unit === "per_share"
						? perShare
						: money(Math.abs(value));
	return value < 0 ? `(${body})` : body;
}

/** The currency a money figure is still in when no rate converted it. */
export function unconvertedCurrency(figure: {
	original_currency?: string | null;
	fx_rate?: number | null;
}): string | null {
	return figure.original_currency && (figure.fx_rate ?? null) === null
		? figure.original_currency
		: null;
}

/** "Financials.xlsx · P&L!F14" or "Agreement.pdf · p. 4" — the tooltip line. */
export function formatSourceLabel(source: DealScreeningSource): string {
	// A web page (DOP-581): its title and where it was read, never a page number.
	if (source.kind === "web") {
		return [source.title || source.filename, "web", source.retrieved_at ? `read ${formatIsoDay(source.retrieved_at)}` : null]
			.filter(Boolean)
			.join(" · ");
	}
	const where =
		source.sheet && source.cell
			? `${source.sheet}!${source.cell}`
			: source.page !== null
				? `p. ${source.page}`
				: null;
	return [source.filename, where, source.ocr ? "OCR" : null]
		.filter(Boolean)
		.join(" · ");
}

/** An ISO timestamp from the API as a local date and time. */
export function formatIsoTimestamp(value: string | null | undefined): string {
	if (!value) return EMPTY_VALUE;
	return formatEpochMs(Date.parse(value));
}

/** An ISO timestamp as a date alone: "Sep 9, 2026". */
export function formatIsoDay(value: string | null | undefined): string {
	if (!value) return EMPTY_VALUE;
	const parsed = Date.parse(value);
	if (!Number.isFinite(parsed)) return EMPTY_VALUE;
	return new Date(parsed).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

/**
 * A calendar date from the API ("2025-12-31") as "Dec 31, 2025". Read in UTC:
 * a balance-sheet date is a day, not a moment, and must not slip to the 30th
 * west of Greenwich.
 */
export function formatCalendarDate(value: string | null | undefined): string {
	if (!value) return EMPTY_VALUE;
	const parsed = Date.parse(value);
	if (!Number.isFinite(parsed)) return EMPTY_VALUE;
	return new Date(parsed).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		timeZone: "UTC",
	});
}

/**
 * The company facts that are true of one day (DOP-661): a share price, a
 * market cap, a NAV per share. Mirrors the backend's
 * `export_docx.HEADER_DATED_KEYS`.
 */
const HEADER_DATED_KEYS = new Set([
	"share_price",
	"market_cap",
	"nav_per_share",
	"discount_to_nav",
	"insider_ownership",
]);
/** And the ones that are true of a fiscal year: "Diluted EPS (FY2025)". */
const HEADER_FISCAL_KEYS = new Set(["diluted_eps"]);

type LabelledFact = Pick<DealScreeningFact, "key" | "label" | "value" | "period_end">;

/**
 * A company fact's label, with the day or the fiscal year its value is as of
 * where it is one that has one and the source dated it: "Share price (Mar 25,
 * 2026)". No date stated, no date shown.
 */
export function headerFactLabel(fact: LabelledFact): string {
	const when = fact.period_end;
	if (!when || fact.value === null || !Number.isFinite(Date.parse(when))) return fact.label;
	if (HEADER_DATED_KEYS.has(fact.key)) return `${fact.label} (${formatCalendarDate(when)})`;
	if (HEADER_FISCAL_KEYS.has(fact.key)) {
		return `${fact.label} (FY${new Date(Date.parse(when)).getUTCFullYear()})`;
	}
	return fact.label;
}

/**
 * Where a listed company trades, as the memo's title line carries it beside
 * the name (DOP-661): "TSX: DC.A", or null for a private deal, an unanswered
 * ticker, or a name that already says it.
 */
export function memoTicker(
	facts: Pick<DealScreeningFact, "key" | "kind" | "state" | "value">[],
	name: string,
): string | null {
	const fact = facts.find((f) => f.kind === "header" && f.key === "ticker");
	if (!fact || fact.state === "gap" || fact.state === "not_available") return null;
	if (typeof fact.value !== "string") return null;
	const ticker = fact.value.trim().replace(/^\(+|\)+$/g, "").trim();
	if (!ticker || name.toLowerCase().includes(ticker.toLowerCase())) return null;
	return ticker;
}

export const countLabel = (count: number, noun: string, plural?: string): string =>
	`${count} ${count === 1 ? noun : (plural ?? `${noun}s`)}`;
